// ─────────────────────────────────────────────────────────────────────────────
// WP-E4 / RF-168 — Conversión de la ventana del Anti-Spoiler entre el
// `<input type="datetime-local">` y el contrato (`AntiSpoilerFrom`/`AntiSpoilerTo`, ISO-8601 UTC).
//
// 🔴 OJO: esto NO es lo mismo que `freezeDates.ts`, y la diferencia es deliberada.
//
// El freeze usa `<input type="date">` y razona en UTC en las DOS direcciones, porque lo que el
// administrador elige ahí es una FECHA CIVIL ("del 10 al 14 de agosto"), no un instante: interpretar
// esa fecha como medianoche local desplazaba la ventana un día entero en cualquier huso positivo.
//
// Aquí lo que elige es un INSTANTE de reloj de pared: "el acto de premiación empieza el 12 a las
// 18:00". Ese "18:00" es 18:00 DONDE ESTÁ QUIEN LO CONFIGURA, y traducirlo a 18:00 UTC —como hace el
// freeze con su medianoche— abriría el anti-spoiler seis horas antes de tiempo en México, que es
// justo la ventana en la que se filtra quién ganó. Por eso `datetime-local` sí va contra la hora
// local: es lo que el propio elemento significa.
//
// La consecuencia práctica es que un administrador en Madrid y otro en Monterrey que tecleen el
// mismo "18:00" configuran instantes distintos. Es lo correcto y es lo que la maqueta sugiere
// (`fundamentales-admin.js:1163` — un solo par Desde/Hasta, sin selector de huso): la ventana es
// global y quien la configura es el Admin Global, uno solo.
// ─────────────────────────────────────────────────────────────────────────────

/** `YYYY-MM-DDTHH:mm` — lo único que emite un `<input type="datetime-local">` con valor.
 *  Se aceptan los segundos porque algunos navegadores los añaden si el paso lo permite. */
const DATETIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Instante del contrato → valor del `<input type="datetime-local">`, en hora LOCAL.
 *
 * Devuelve cadena vacía ante `null`/`undefined` y también ante una fecha que el navegador no sepa
 * interpretar: un `value` con forma inesperada deja el control en un estado que no se puede corregir
 * tecleando, y es peor que verlo vacío.
 */
export function isoToDateTimeLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Valor del `<input type="datetime-local">` → instante del contrato (ISO-8601 en UTC).
 *
 * `new Date('2026-08-12T18:00')` —sin `Z` ni offset— es hora local por definición del lenguaje, que
 * es exactamente la semántica del control. Vacío ⇒ `null`, y eso significa «sin límite por ese
 * lado», no «ventana a medias»: el anti-spoiler admite media ventana (solo Desde, o solo Hasta).
 */
export function dateTimeLocalToIso(value: string): string | null {
  if (!DATETIME_LOCAL.test(value)) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // 🔴 `new Date('2026-02-30T18:00')` NO es una fecha inválida en JavaScript: RUEDA sola al 3 de
  // marzo, en silencio. Un `<input type="datetime-local">` no emite un 30 de febrero, pero el
  // helper también convierte valores que vienen de otro sitio, y una ventana que se desplaza tres
  // días sin que nadie la toque es exactamente el fallo silencioso que este trabajo vino a cerrar.
  // Se compara contra lo tecleado: si el motor movió algo, no se persiste.
  return isoToDateTimeLocal(d.toISOString()) === value.slice(0, 16) ? d.toISOString() : null;
}

/**
 * ¿La ventana está invertida (`Desde` posterior a `Hasta`)?
 *
 * 🔴 Se avisa EN LA PANTALLA además de rechazarlo el backend, porque una ventana invertida no es
 * media configuración: es una que NO PROTEGE NUNCA. Sin el aviso, el administrador ve el toggle
 * encendido, se va tranquilo, y el anti-spoiler está apagado de hecho el día de la premiación.
 */
export function ventanaInvertida(desde?: string | null, hasta?: string | null): boolean {
  if (!desde || !hasta) return false;
  const a = new Date(desde).getTime();
  const b = new Date(hasta).getTime();
  return !Number.isNaN(a) && !Number.isNaN(b) && a > b;
}
